/**
 * The WATCHLIST/ROSTER row of the redesigned add-players drawer (PC5/PC6) —
 * the shared {@link CompactPlayerRow} presentation, kept as its own view so
 * the drag shell wrapper (`WatchlistPlayerCardContainer`) has a stable body
 * to render.
 *
 * The PC frames draw these tabs as FLAT rows: initials avatar, name over a
 * meta caption, an inline "GPR n" info pill, and a compact solid-accent
 * **+ Add** — a player already in this pipeline mutes and states "In list"
 * instead. The SE-6554 bordered card chrome is gone with the redesign.
 * @remarks View component — no business logic, hooks, or state.
 * @module features/player-kanban/components/molecules/AddPlayersRow
 */

import React, { memo } from "react";

import { CompactPlayerRowView as CompactPlayerRow } from "../CompactPlayerRow";

/** Props for {@link WatchlistPlayerCardView}. */
export interface WatchlistPlayerCardViewProps {
  /** Player id — the add target and the row's test identity. */
  readonly playerId: string;
  /** Display name. */
  readonly name: string;
  /** Pre-composed meta caption (watchlist or roster variant). */
  readonly meta: string;
  /** GPR score for the inline info pill; `null` omits the pill. */
  readonly gpr?: string | null;
  /** Whether the player already sits in this pipeline (muted "In list"). */
  readonly isInList?: boolean;
  /** Whether an add for this player is in flight. */
  readonly isAdding?: boolean;
  /** Add this player to the pipeline. Absent hides the action. */
  readonly onAdd?: () => void;
}

/**
 * Render one Watchlists / My Roster row.
 * @param root0 - Component props.
 * @param root0.playerId - Player id.
 * @param root0.name - Display name.
 * @param root0.meta - Meta caption.
 * @param root0.gpr - GPR score for the pill.
 * @param root0.isInList - Whether the player is already in this pipeline.
 * @param root0.isAdding - Whether an add is in flight.
 * @param root0.onAdd - Add callback (SE-6951).
 */
const WatchlistPlayerCardView = ({
  playerId,
  name,
  meta,
  gpr,
  isInList,
  isAdding,
  onAdd,
}: WatchlistPlayerCardViewProps) => (
  <CompactPlayerRow
    playerId={playerId}
    name={name}
    meta={meta}
    gpr={gpr}
    isInList={isInList}
    isAdding={isAdding}
    onAdd={onAdd}
  />
);

WatchlistPlayerCardView.displayName = "WatchlistPlayerCardView";

export default memo(WatchlistPlayerCardView);
